import {
  Component,
  Input,
  Output,
  EventEmitter,
  computed,
  signal,
  OnInit,
  OnDestroy,
  AfterViewInit,
  ElementRef,
  ViewChild,
} from '@angular/core';

import { cva } from '../utils/class-variance.util';
import { VariantConfig } from '../models/class-variance.model';
import { ThemeSection } from '../services/theme.service';
import { IconGlassComponent } from '../icon/icon-glass.component';
import { DrawerVariant, DrawerPosition, DrawerMode, DrawerSize } from '../models/drawer.model';
import { LoggerService } from '../services/logger.service';

const drawerVariants = cva({
  base: [
    'fixed z-50 transition-all duration-300 ease-out',
    'backdrop-blur-xl border transform-gpu',
    // Glass morphism base
    'before:content-[""] before:absolute before:inset-0',
    'before:bg-glass-gradient-light dark:before:bg-glass-gradient-dark',
    'before:opacity-70 before:pointer-events-none',
    'before:transition-opacity before:duration-300',
  ].join(' '),
  variants: {
    variant: {
      default: [
        'bg-linear-to-br from-slate-800/92 via-slate-900/94 to-blue-950/88',
        'border border-blue-200/14',
        'shadow-2xl shadow-slate-950/36',
      ].join(' '),
      elevated: [
        'bg-linear-to-br from-slate-800/94 via-slate-900/95 to-blue-950/90',
        'border border-blue-200/16',
        'shadow-2xl shadow-slate-950/40',
      ].join(' '),
      premium: [
        'bg-[radial-gradient(circle_at_top_right,rgba(37,99,235,0.14),transparent_34%),radial-gradient(circle_at_bottom_left,rgba(59,130,246,0.08),transparent_40%),linear-gradient(180deg,rgba(24,40,66,0.96)_0%,rgba(10,18,34,0.98)_100%)]',
        'border border-[rgba(146,176,218,0.18)]',
        'shadow-[inset_0_1px_0_rgba(255,255,255,0.05),0_24px_60px_rgba(2,8,23,0.42),0_0_60px_rgba(37,99,235,0.08)]',
      ].join(' '),
      minimal: [
        'bg-linear-to-br from-slate-800/86 via-slate-900/90 to-blue-950/84',
        'border border-blue-200/10',
        'shadow-xl shadow-slate-950/24',
      ].join(' '),
    },
    position: {
      left: 'top-0 left-0 h-full',
      right: 'top-0 right-0 h-full',
      top: 'top-0 left-0 w-full',
      bottom: 'bottom-0 left-0 w-full',
    },
    size: {
      sm: '',
      md: '',
      lg: '',
      xl: '',
      full: '',
    },
    open: {
      true: '',
      false: '',
    },
  },
  compoundVariants: [
    // Left/Right sizing
    { position: ['left', 'right'], size: 'sm', class: 'w-64 max-w-64' },
    { position: ['left', 'right'], size: 'md', class: 'w-80 max-w-80' },
    { position: ['left', 'right'], size: 'lg', class: 'w-96 max-w-96' },
    { position: ['left', 'right'], size: 'xl', class: 'w-md max-w-md' },
    { position: ['left', 'right'], size: 'full', class: 'w-screen max-w-screen' },

    // Top/Bottom sizing
    { position: ['top', 'bottom'], size: 'sm', class: 'h-48' },
    { position: ['top', 'bottom'], size: 'md', class: 'h-64' },
    { position: ['top', 'bottom'], size: 'lg', class: 'h-80' },
    { position: ['top', 'bottom'], size: 'xl', class: 'h-96' },
    { position: ['top', 'bottom'], size: 'full', class: 'h-screen' },

    // Open/Closed transforms for left
    { position: 'left', open: 'true', class: 'translate-x-0' },
    { position: 'left', open: 'false', class: '-translate-x-full' },

    // Open/Closed transforms for right
    { position: 'right', open: 'true', class: 'translate-x-0' },
    { position: 'right', open: 'false', class: 'translate-x-full' },

    // Open/Closed transforms for top
    { position: 'top', open: 'true', class: 'translate-y-0' },
    { position: 'top', open: 'false', class: '-translate-y-full' },

    // Open/Closed transforms for bottom
    { position: 'bottom', open: 'true', class: 'translate-y-0' },
    { position: 'bottom', open: 'false', class: 'translate-y-full' },
  ],
  defaultVariants: {
    variant: 'default',
    position: 'left',
    size: 'md',
    open: 'false',
  },
} as VariantConfig<Record<string, Record<string, string>>>);

const backdropVariants = cva({
  base: ['fixed inset-0 z-40 transition-all duration-300 ease-out', 'backdrop-blur-sm'].join(' '),
  variants: {
    variant: {
      default: 'bg-[linear-gradient(180deg,rgba(2,8,23,0.52)_0%,rgba(7,18,37,0.42)_100%)]',
      elevated: 'bg-[linear-gradient(180deg,rgba(2,8,23,0.58)_0%,rgba(7,18,37,0.48)_100%)]',
      premium:
        'bg-[radial-gradient(circle_at_right,rgba(37,99,235,0.14),transparent_28%),linear-gradient(180deg,rgba(2,8,23,0.62)_0%,rgba(7,18,37,0.5)_100%)]',
      minimal: 'bg-[linear-gradient(180deg,rgba(2,8,23,0.46)_0%,rgba(7,18,37,0.38)_100%)]',
    },
    visible: {
      true: 'opacity-100 pointer-events-auto',
      false: 'opacity-0 pointer-events-none',
    },
  },
  defaultVariants: {
    variant: 'default',
    visible: 'false',
  },
});

const contentVariants = cva({
  base: ['relative z-10 h-full flex flex-col overflow-hidden'].join(' '),
  variants: {
    position: {
      left: 'border-r-0',
      right: 'border-l-0',
      top: 'border-b-0',
      bottom: 'border-t-0',
    },
  },
  defaultVariants: {
    position: 'left',
  },
});

@Component({
  selector: 'ui-drawer-glass',
  standalone: true,
  imports: [IconGlassComponent],
  templateUrl: './drawer-glass.component.html',
  host: {
    style: 'display: none;',
  },
})
export class DrawerGlassComponent implements OnInit, OnDestroy, AfterViewInit {
  @ViewChild('drawerElement') drawerElement!: ElementRef;

  @Input() variant: DrawerVariant = 'default';
  @Input() position: DrawerPosition = 'left';
  @Input() mode: DrawerMode = 'over';
  @Input() size: DrawerSize = 'md';
  @Input() showBackdrop: boolean = true;
  @Input() closeOnBackdrop: boolean = true;
  @Input() closeOnEscape: boolean = true;
  @Input() hasHeader: boolean = true;
  @Input() hasFooter: boolean = false;
  @Input() hasCustomHeader: boolean = false;
  @Input() title: string = '';
  @Input() subtitle: string = '';
  @Input() showCloseButton: boolean = true;
  @Input() scrollContent: boolean = true;
  @Input() resizable: boolean = false;
  @Input() ariaLabel: string = 'Navigation drawer';
  @Input() themeSection: ThemeSection = 'global';

  @Output() opened = new EventEmitter<void>();
  @Output() closed = new EventEmitter<void>();
  @Output() backdropClick = new EventEmitter<void>();

  public isOpen = signal(false);
  public isInitialized = signal(false);
  public isVisible = signal(false);
  private isResizing = false;
  private startX = 0;
  private startWidth = 0;
  private previouslyFocusedElement: HTMLElement | null = null;
  private savedBodyOverflow: string = '';
  private savedBodyPaddingRight: string = '';
  // Bound handlers are stored so we can remove the exact same function reference
  private boundHandleGlobalKeyDown = this.handleGlobalKeyDown.bind(this);
  private boundHandleMouseMove = this.handleMouseMove.bind(this);
  private boundHandleMouseUp = this.handleMouseUp.bind(this);
  private boundHandleFocusTrap = this.handleFocusTrap.bind(this);
  private visibilityTimeout: ReturnType<typeof setTimeout> | null = null;

  constructor(
    private readonly loggerService: LoggerService,
    private readonly hostElement: ElementRef<HTMLElement>,
  ) {}

  // Runtime helpers that compute classes based on current (non-signal) inputs.
  // These are used by tests and templates when inputs change at runtime.
  public getDrawerClasses(): string {
    const openState = this.isOpen() ? 'true' : 'false';
    return drawerVariants({
      variant: this.variant,
      position: this.position,
      size: this.size,
      open: openState,
    });
  }

  public getBackdropClasses(): string {
    return backdropVariants({
      variant: this.variant,
      visible: this.isOpen() ? 'true' : 'false',
    });
  }

  public getContentClasses(): string {
    return contentVariants({
      position: this.position,
    });
  }

  public getResizeHandleClasses(): string {
    const baseClasses = 'absolute';
    switch (this.position) {
      case 'left':
        return `${baseClasses} top-0 right-0 w-1 h-full!`;
      case 'right':
        return `${baseClasses} top-0 left-0 w-1 h-full!`;
      case 'top':
        return `${baseClasses} bottom-0 left-0 w-full h-1!`;
      case 'bottom':
        return `${baseClasses} top-0 left-0 w-full h-1!`;
      default:
        return baseClasses;
    }
  }

  public computedDrawerClasses = computed(() => {
    const openState = this.isOpen() ? 'true' : 'false';
    const classes = drawerVariants({
      variant: this.variant,
      position: this.position,
      size: this.size,
      open: openState,
    });
    this.loggerService.log('Computed drawer classes:', { openState, classes });
    return classes;
  });

  public computedBackdropClasses = computed(() => {
    return backdropVariants({
      variant: this.variant,
      visible: this.isOpen() ? 'true' : 'false',
    });
  });

  public computedContentClasses = computed(() => {
    return contentVariants({
      position: this.position,
    });
  });

  public resizeHandleClasses = computed(() => {
    const baseClasses = 'absolute';
    switch (this.position) {
      case 'left':
        return `${baseClasses} top-0 right-0 w-1 h-full!`;
      case 'right':
        return `${baseClasses} top-0 left-0 w-1 h-full!`;
      case 'top':
        return `${baseClasses} bottom-0 left-0 w-full h-1!`;
      case 'bottom':
        return `${baseClasses} top-0 left-0 w-full h-1!`;
      default:
        return baseClasses;
    }
  });

  ngOnInit(): void {
    if (typeof document !== 'undefined') {
      document.addEventListener('keydown', this.boundHandleGlobalKeyDown);
      document.addEventListener('keydown', this.boundHandleFocusTrap);
      document.addEventListener('mousemove', this.boundHandleMouseMove);
      document.addEventListener('mouseup', this.boundHandleMouseUp);
    }
  }

  ngAfterViewInit(): void {
    this.isInitialized.set(true);
    this.isVisible.set(this.isOpen());
    setTimeout(() => {
      this.hostElement.nativeElement.style.display = 'block';
    }, 0);
  }

  ngOnDestroy(): void {
    if (this.visibilityTimeout) {
      clearTimeout(this.visibilityTimeout);
    }
    if (typeof document !== 'undefined') {
      document.removeEventListener('keydown', this.boundHandleGlobalKeyDown);
      document.removeEventListener('keydown', this.boundHandleFocusTrap);
      document.removeEventListener('mousemove', this.boundHandleMouseMove);
      document.removeEventListener('mouseup', this.boundHandleMouseUp);
    }
    this.unlockScroll();
    this.restoreFocus();
  }

  open(): void {
    if (this.visibilityTimeout) {
      clearTimeout(this.visibilityTimeout);
      this.visibilityTimeout = null;
    }

    // Save the currently focused element so we can restore it on close
    if (typeof document !== 'undefined') {
      this.previouslyFocusedElement = document.activeElement as HTMLElement;
    }

    this.isVisible.set(true);
    this.isOpen.set(true);
    this.lockScroll();
    this.opened.emit();

    // After the drawer transition completes, move focus to the first focusable element
    setTimeout(() => {
      this.focusFirstElement();
    }, 300);
  }

  close(): void {
    this.isOpen.set(false);
    this.unlockScroll();
    this.restoreFocus();
    if (this.visibilityTimeout) {
      clearTimeout(this.visibilityTimeout);
    }
    this.visibilityTimeout = setTimeout(() => {
      if (!this.isOpen()) {
        this.isVisible.set(false);
      }
    }, 300);
    this.closed.emit();
  }

  handleCloseClick(event: Event): void {
    event.preventDefault();
    event.stopPropagation();
    this.close();
  }

  getTransform(): string {
    if (this.isOpen()) {
      return 'translate3d(0, 0, 0)';
    }

    switch (this.position) {
      case 'left':
        return 'translate3d(-100%, 0, 0)';
      case 'right':
        return 'translate3d(100%, 0, 0)';
      case 'top':
        return 'translate3d(0, -100%, 0)';
      case 'bottom':
        return 'translate3d(0, 100%, 0)';
      default:
        return 'translate3d(-100%, 0, 0)';
    }
  }

  toggle(): void {
    if (this.isOpen()) {
      this.close();
    } else {
      this.open();
    }
  }

  public handleKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.closeOnEscape) {
      event.preventDefault();
      this.close();
    }
  }

  private handleGlobalKeyDown(event: KeyboardEvent): void {
    if (event.key === 'Escape' && this.closeOnEscape && this.isOpen()) {
      this.close();
    }
  }

  public startResize(event: MouseEvent): void {
    if (!this.resizable) return;

    event.preventDefault();
    this.isResizing = true;
    this.startX = event.clientX;

    const rect = this.drawerElement.nativeElement.getBoundingClientRect();
    this.startWidth = rect.width;
  }

  private handleMouseMove(event: MouseEvent): void {
    if (!this.isResizing) return;

    const deltaX = event.clientX - this.startX;
    let newWidth = this.startWidth;

    if (this.position === 'left') {
      newWidth = this.startWidth + deltaX;
    } else if (this.position === 'right') {
      newWidth = this.startWidth - deltaX;
    }

    // Apply constraints
    const minWidth = 200;
    const maxWidth = window.innerWidth * 0.8;
    newWidth = Math.max(minWidth, Math.min(maxWidth, newWidth));

    this.drawerElement.nativeElement.style.width = `${newWidth}px`;
  }

  private handleMouseUp(): void {
    this.isResizing = false;
  }

  private handleFocusTrap(event: KeyboardEvent): void {
    if (event.key !== 'Tab' || !this.isOpen()) return;

    const focusableElements = this.getFocusableElements();
    if (focusableElements.length === 0) return;

    const firstElement = focusableElements[0]!;
    const lastElement = focusableElements[focusableElements.length - 1]!;

    if (event.shiftKey) {
      // Shift+Tab: if focus is on first element, wrap to last
      if (document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
    } else {
      // Tab: if focus is on last element, wrap to first
      if (document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  }

  private getFocusableElements(): HTMLElement[] {
    if (!this.drawerElement?.nativeElement) return [];

    const selector = [
      'a[href]:not([disabled]):not([tabindex="-1"])',
      'button:not([disabled]):not([tabindex="-1"])',
      'textarea:not([disabled]):not([tabindex="-1"])',
      'input:not([disabled]):not([tabindex="-1"])',
      'select:not([disabled]):not([tabindex="-1"])',
      '[tabindex]:not([tabindex="-1"]):not([disabled])',
    ].join(', ');

    return Array.from(this.drawerElement.nativeElement.querySelectorAll(selector)) as HTMLElement[];
  }

  private focusFirstElement(): void {
    const focusableElements = this.getFocusableElements();
    const firstFocusable = focusableElements[0];
    if (firstFocusable) {
      firstFocusable.focus();
    } else if (this.drawerElement?.nativeElement) {
      this.drawerElement.nativeElement.focus();
    }
  }

  private restoreFocus(): void {
    if (this.previouslyFocusedElement && typeof this.previouslyFocusedElement.focus === 'function') {
      this.previouslyFocusedElement.focus();
      this.previouslyFocusedElement = null;
    }
  }

  private lockScroll(): void {
    if (typeof document !== 'undefined') {
      const scrollbarWidth = Math.max(0, window.innerWidth - document.documentElement.clientWidth);
      const existingPaddingRight = Number.parseFloat(document.body.style.paddingRight || '0') || 0;
      this.savedBodyOverflow = document.body.style.overflow;
      this.savedBodyPaddingRight = document.body.style.paddingRight;
      document.body.style.overflow = 'hidden';
      if (scrollbarWidth > 0) {
        document.body.style.paddingRight = `${existingPaddingRight + scrollbarWidth}px`;
      }
    }
  }

  private unlockScroll(): void {
    if (typeof document !== 'undefined') {
      document.body.style.overflow = this.savedBodyOverflow;
      document.body.style.paddingRight = this.savedBodyPaddingRight;
      this.savedBodyOverflow = '';
      this.savedBodyPaddingRight = '';
    }
  }
}

/**
 * Usage Examples:
 *
 * Basic navigation drawer:
 * <ui-drawer-glass
 *   title="Navigation"
 *   subtitle="Main menu"
 *   #drawer
 * >
 *   <nav class="p-6">
 *     <a href="/home" class="block py-2">Home</a>
 *     <a href="/products" class="block py-2">Products</a>
 *     <a href="/about" class="block py-2">About</a>
 *   </nav>
 * </ui-drawer-glass>
 *
 * Premium e-commerce cart drawer:
 * <ui-drawer-glass
 *   title="Shopping Cart"
 *   subtitle="3 items"
 *   variant="premium"
 *   position="right"
 *   size="lg"
 *   [hasFooter]="true"
 *   #cartDrawer
 * >
 *   <div class="p-6">
 *     <!-- Cart items -->
 *   </div>
 *
 *   <div slot="footer" class="flex justify-between items-center">
 *     <span class="text-lg font-semibold">Total: $299.99</span>
 *     <ui-button-glass variant="primary">Checkout</button>
 *   </div>
 * </ui-drawer-glass>
 *
 * Resizable side panel:
 * <ui-drawer-glass
 *   title="Product Filters"
 *   variant="elevated"
 *   [resizable]="true"
 *   size="sm"
 *   mode="side"
 *   [showBackdrop]="false"
 * >
 *   <div class="p-6">
 *     <!-- Filter controls -->
 *   </div>
 * </ui-drawer-glass>
 *
 * Top notification drawer:
 * <ui-drawer-glass
 *   title="Notifications"
 *   position="top"
 *   size="md"
 *   variant="minimal"
 *   [closeOnBackdrop]="true"
 * >
 *   <div class="p-6">
 *     <!-- Notification list -->
 *   </div>
 * </ui-drawer-glass>
 *
 * Control from template:
 * <ui-button-glass (click)="drawer.toggle()">Toggle Menu</button>
 * <ui-button-glass (click)="cartDrawer.open()">View Cart</button>
 *
 * Different variants:
 * <ui-drawer-glass variant="elevated" title="Elevated Drawer"></ui-drawer-glass>
 * <ui-drawer-glass variant="minimal" title="Minimal Drawer"></ui-drawer-glass>
 * <ui-drawer-glass variant="premium" title="Premium Drawer"></ui-drawer-glass>
 */
